import {
  AppToaster,
  ReactBaseConfigProvider,
  ReleaseNotificationBar,
  type ReactBaseConfig,
  useAuth
} from '@invara-dev/react-base';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import axios from 'axios';
import { useEffect } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { ErrorContainer } from '../ErrorContainer';
import { Box } from './components/ui/chakraPrimitives.ts';
import { CompassUser } from './globalTypes/user/compassUser.ts';
import { useNavWithRouting } from './routes/AppRouter';
import { ChakraRootProvider } from './components/ui/provider.tsx';
import { API_URL } from './util/config.ts';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnMount: true,
      refetchOnWindowFocus: true
    }
  }
});

const reactBaseConfig: ReactBaseConfig = {
  baseTable: {
    actionColumnPosition: 'left'
  },
  baseToolbar: {
    contentSized: false
  },
  forms: {
    variant: 'legacy',
    dateInput: {
      mode: 'picker'
    }
  }
};

export const App = () => {
  const { initUser, hasLoaded } = useAuth<CompassUser>();
  const AppRouter = createBrowserRouter(useNavWithRouting());
  axios.defaults.baseURL = API_URL;
  axios.defaults.withCredentials = true;
  useEffect(() => {
    async function initAuth() {
      await initUser();
    }

    initAuth();
  }, []);

  return (
    <ReactBaseConfigProvider config={reactBaseConfig}>
      <ChakraRootProvider>
        <Box maxHeight={'100vh'}>
          <QueryClientProvider client={queryClient}>
            <AppToaster />
            <ReleaseNotificationBar apiBaseUrl={API_URL} />
            <ErrorContainer>{hasLoaded && <RouterProvider router={AppRouter} />}</ErrorContainer>

            <ReactQueryDevtools />
          </QueryClientProvider>
        </Box>
      </ChakraRootProvider>
    </ReactBaseConfigProvider>
  );
};
